import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const COMPANY = {
  name: "Build & Service Kft.",
  brand: "Autó-Szerelem — Séra Autószervíz",
  seat: "4034 Debrecen, Mészöly Géza u. 3.",
  workshop: "4031 Debrecen, Vadvirág utca 15/D — 3. kapu",
  taxNumber: "24992392-2-09",
  registrationNumber: "09-09-026253",
  representative: "Séra Attila",
  phone: "+36 30 863 7476",
  phoneRaw: "+36308637476",
  email: "autoszerelem@gmail.com",
  website: "https://autoszerelem.hu",
  hours: "Hétfő – Péntek: 08:00 – 17:00",
  registryAuthority: "Debreceni Törvényszék Cégbírósága",
};

export default function Impresszum() {
  useDocumentMeta({
    title: "Impresszum | Autó-Szerelem — Séra Autószervíz",
    description:
      "Az Autó-Szerelem (Build & Service Kft.) cég impresszuma — cégadatok, székhely, telephely, adószám, cégjegyzékszám, kapcsolat. Debrecen.",
    path: "/impresszum",
  });

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="impresszum-page">
      <Navbar />
      <main className="pt-24 sm:pt-28 pb-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-white mb-10 transition-colors"
            data-testid="impresszum-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Vissza a főoldalra
          </Link>

          <div className="flex items-center gap-4 mb-6">
            <div className="m-stripe h-1 w-24" />
            <span
              className="text-[0.72rem] font-bold uppercase tracking-[0.32em] text-zinc-300"
              style={{ fontFamily: "Barlow Condensed" }}
            >
              Jogi információ
            </span>
          </div>

          <h1
            className="font-black uppercase text-white leading-[0.95] tracking-tight mb-10"
            style={{
              fontFamily: "Barlow Condensed",
              fontSize: "clamp(2.5rem, 6vw, 4.5rem)",
            }}
          >
            Impresszum
          </h1>

          <div className="space-y-10 text-zinc-300 leading-relaxed">
            <Block title="A weboldal üzemeltetője">
              <Row label="Cégnév" value={COMPANY.name} />
              <Row label="Márkanév" value={COMPANY.brand} />
              <Row label="Képviselő" value={COMPANY.representative} />
              <Row label="Székhely" value={COMPANY.seat} />
              <Row label="Telephely (műhely)" value={COMPANY.workshop} />
              <Row label="Adószám" value={COMPANY.taxNumber} mono />
              <Row label="Cégjegyzékszám" value={COMPANY.registrationNumber} mono />
              <Row label="Cégbíróság" value={COMPANY.registryAuthority} />
            </Block>

            <Block title="Kapcsolat">
              <Row
                label="Telefon"
                value={
                  <a
                    href={`tel:${COMPANY.phoneRaw}`}
                    className="hover:text-[#0082D2] transition-colors"
                  >
                    {COMPANY.phone}
                  </a>
                }
              />
              <Row
                label="E-mail"
                value={
                  <a
                    href={`mailto:${COMPANY.email}`}
                    className="hover:text-[#0082D2] transition-colors"
                  >
                    {COMPANY.email}
                  </a>
                }
              />
              <Row
                label="Weboldal"
                value={
                  <a
                    href={COMPANY.website}
                    className="hover:text-[#0082D2] transition-colors"
                  >
                    {COMPANY.website}
                  </a>
                }
              />
              <Row label="Nyitvatartás" value={COMPANY.hours} />
            </Block>

            <Block title="Tárhelyszolgáltató">
              <p className="text-sm">
                A weboldal tárhelyét és infrastruktúráját az{" "}
                <strong className="text-white">Emergent (emergent.sh)</strong>{" "}
                szolgáltatja, Cloudflare CDN-en keresztül. A domain regisztrátora
                a <strong className="text-white">Rackhost Zrt.</strong> (6722
                Szeged, Tisza Lajos krt. 41.).
              </p>
            </Block>

            <Block title="Szellemi tulajdon">
              <p className="text-sm">
                A weboldalon megjelenő tartalmak (szövegek, képek, grafikák,
                logók, leírások) — kivéve a partnerlogókat (pl. Q-Service,
                Motul) — a {COMPANY.name} szellemi tulajdonát képezik.
                Engedély nélküli felhasználásuk tilos.
              </p>
              <p className="text-sm mt-3">
                A Q-Service és a Motul / MotulEVO bejegyzett védjegyek a
                jogtulajdonosaiké; használatuk a hivatalos partneri státusz
                alapján történik.
              </p>
            </Block>

            <Block title="Felelősségkorlátozás">
              <p className="text-sm">
                A weboldalon szereplő szolgáltatás-leírások és árak tájékoztató
                jellegűek; a végleges ajánlatot a műhelyben történt
                állapotfelmérés és egyeztetés után adjuk. A weboldal tartalmát
                folyamatosan frissítjük, de a teljesség és pontosság érdekében
                a foglalás előtt javasoljuk az{" "}
                <a
                  href={`tel:${COMPANY.phoneRaw}`}
                  className="text-[#0082D2] hover:underline"
                >
                  egyeztetést telefonon
                </a>
                .
              </p>
            </Block>

            <div className="pt-6 border-t border-white/10 text-xs text-zinc-500">
              Utolsó frissítés: 2026. január
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Block({ title, children }) {
  return (
    <section>
      <h2
        className="text-2xl font-bold uppercase text-white tracking-tight mb-5 pb-3 border-b border-white/10"
        style={{ fontFamily: "Barlow Condensed" }}
      >
        {title}
      </h2>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 py-2 border-b border-white/5 last:border-b-0">
      <span
        className="text-[0.7rem] font-bold uppercase tracking-[0.18em] text-zinc-500"
        style={{ fontFamily: "Barlow Condensed" }}
      >
        {label}
      </span>
      <span className={`sm:col-span-2 text-zinc-200 ${mono ? "mono" : ""}`}>
        {value}
      </span>
    </div>
  );
}
